import { useState, useEffect, useCallback, useRef } from 'react';
import { useSessionsAwaitingDecision } from '../hooks/use-calendar-proposals.js';
import { NextLogin } from '../components/server-logins.js';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import type { ReplaySpeed } from '../hooks/use-session-replay.js';
import { useSession, useSessions, useSessionWaits, useRenameSession } from '../hooks/use-sessions.js';
import { useSessionHistory } from '../hooks/use-session-history.js';
import { SessionCardsContext } from '../components/slack-proposal-card.js';
import { useSessionProposalCards } from '../components/session-proposals.js';
import { useMarkRead } from '../hooks/use-read-state.js';
import { useSessionOutput } from '../hooks/use-session-output.js';
import { useSessionReplay } from '../hooks/use-session-replay.js';
import { useServer } from '../hooks/use-servers.js';
import { useStartServer } from '../hooks/use-server-actions.js';
import { SessionStatusBadge } from '../components/session-status-badge.js';
import { EventTimeline } from '../components/event-timeline.js';
import { SessionControls, InterruptButton } from '../components/session-controls.js';
import { ReplayControls } from '../components/replay-controls.js';
import { Button } from '../components/button.js';
import { useAuth } from '../auth/index.js';
import { useSwitchModel } from '../hooks/use-session-actions.js';
import { SessionFacets } from '../components/session-facets.js';

const MODEL_LABELS: Record<string, string> = {
  opus: 'Opus',
  sonnet: 'Sonnet',
  haiku: 'Haiku',
};

function formatTokens(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}k`;
  return String(n);
}

/** The session that started this one (#968), by its name, to go back to it */
function StartedByLink({ serverId, parentId }: { serverId: string; parentId: string }) {
  const { data: parent } = useSession(serverId, parentId);
  const name = parent?.name || `Session ${parentId.slice(0, 8)}`;
  return (
    <Link
      to={`/servers/${serverId}/sessions/${parentId}`}
      className="rounded text-xs underline-offset-2 hover:underline focus-visible:outline-2 focus-visible:outline-offset-2"
      style={{ color: 'var(--color-text-2)', outlineColor: 'var(--color-brand)' }}
      data-testid="started-by"
    >
      Started from {name}
    </Link>
  );
}

/** The sessions this one started (#968), each by its name, to follow them */
function StartedSessionsLinks({ serverId, sessionId }: { serverId: string; sessionId: string }) {
  const { data: sessions } = useSessions(serverId);
  const started = (sessions ?? []).filter((s) => s.startedBy === sessionId);
  if (started.length === 0) return null;
  return (
    <span className="text-xs" style={{ color: 'var(--color-text-3)' }} data-testid="started-sessions">
      Started{' '}
      {started.map((s, i) => (
        <span key={s.id}>
          {i > 0 && ', '}
          <Link
            to={`/servers/${serverId}/sessions/${s.id}`}
            className="rounded underline-offset-2 hover:underline focus-visible:outline-2 focus-visible:outline-offset-2"
            style={{ color: 'var(--color-text-2)', outlineColor: 'var(--color-brand)' }}
          >
            {s.name || `Session ${s.id.slice(0, 8)}`}
          </Link>
        </span>
      ))}
    </span>
  );
}

/** What the session waits for (#965): the checks of a commit, each linked to them on GitHub */
function SessionWaits({ serverId, sessionId }: { serverId: string; sessionId: string }) {
  const { data: waits } = useSessionWaits(serverId, sessionId);
  if (!waits?.length) return null;
  return (
    <>
      {waits.map((w) => {
        const until = new Date(w.deadline).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
        if (w.kind === 'command') {
          // A command the server's agent runs for the session (ADR-032): shortened here, whole on hover
          const short = w.check.length > 48 ? `${w.check.slice(0, 47)}…` : w.check;
          return (
            <span key={w.id} className="text-xs" style={{ color: 'var(--color-text-2)' }} title={`${w.check}\nuntil: ${w.until}`} data-testid="session-wait">
              Waiting for <code className="rounded px-1" style={{ backgroundColor: 'var(--color-surface-2)' }}>{short}</code>, until {until}
            </span>
          );
        }
        return (
          <a
            key={w.id}
            href={`https://github.com/${w.repo}/commit/${w.sha}/checks`}
            target="_blank"
            rel="noopener noreferrer"
            className="rounded text-xs underline-offset-2 hover:underline focus-visible:outline-2 focus-visible:outline-offset-2"
            style={{ color: 'var(--color-text-2)', outlineColor: 'var(--color-brand)' }}
            data-testid="session-wait"
          >
            Waiting for the checks of {w.repo.split('/')[1]}@{w.sha.slice(0, 7)}, until {until}
          </a>
        );
      })}
    </>
  );
}

function formatDuration(session: { createdAt: string; duration?: number; status: string }): string {
  if (session.duration) {
    const mins = Math.floor(session.duration / 60);
    const secs = session.duration % 60;
    return mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
  }
  if (session.status === 'terminated') return '';
  const elapsed = Math.floor((Date.now() - new Date(session.createdAt).getTime()) / 1000);
  const mins = Math.floor(elapsed / 60);
  const secs = elapsed % 60;
  return mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
}

export function SessionDetail() {
  const { serverId, sessionId } = useParams<{ serverId: string; sessionId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const [replayManual, setReplayManual] = useState(false);
  const [scrolledDown, setScrolledDown] = useState(false);
  const [headerHovered, setHeaderHovered] = useState(false);
  const [headerPinned, setHeaderPinned] = useState(false);
  const hoverTimeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const headerCompact = scrolledDown && !headerHovered && !headerPinned;

  const { user } = useAuth();
  const awaitingDecision = useSessionsAwaitingDecision(serverId!).has(sessionId!);
  const isAdmin = user?.role === 'admin';
  const { data: server } = useServer(serverId!);
  const serverStopped = server?.status === 'stopped';
  const startServer = useStartServer();
  const { data: session, isLoading: sessionLoading, isError, error } = useSession(serverId!, sessionId!);
  const switchModel = useSwitchModel(serverId!, sessionId!);
  const renameSession = useRenameSession(serverId!, sessionId!);
  const [showModelMenu, setShowModelMenu] = useState(false);
  const [isEditingName, setIsEditingName] = useState(false);
  const [editNameValue, setEditNameValue] = useState('');
  const nameInputRef = useRef<HTMLInputElement>(null);
  const isLive = session?.status === 'processing' || session?.status === 'waiting_for_input' || session?.status === 'idle' || session?.status === 'active';
  const replayFromUrl = searchParams.get('replay') === 'true';
  const isReplayMode = (replayFromUrl || replayManual) && !isLive;

  const { isStreaming } = useSessionOutput(serverId!, sessionId!, session?.status);
  const { data: events, isLoading: eventsLoading, reset: resetHistory } = useSessionHistory(serverId!, sessionId!, session?.status, isStreaming);
  // What it proposed (ADR-039 8c): Slack messages, calendar and Drive changes, each on a card after the call that made it
  const { cardAfter, unpaired } = useSessionProposalCards(serverId!, sessionId!, events, isAdmin);
  // Reading the session marks it read, again as new events arrive while it is on screen
  const { markRead } = useMarkRead(serverId!);
  const eventCount = events?.length ?? 0;
  useEffect(() => {
    if (!sessionId || document.visibilityState === 'hidden') return;
    const timer = setTimeout(() => markRead([sessionId]), 2_000);
    return () => clearTimeout(timer);
  }, [sessionId, eventCount, markRead]);
  const replay = useSessionReplay(serverId!, sessionId!);

  const enterReplay = useCallback(() => {
    setReplayManual(true);
    setSearchParams({ replay: 'true' });
  }, [setSearchParams]);

  const exitReplay = useCallback(() => {
    setReplayManual(false);
    replay.reset();
    searchParams.delete('replay');
    setSearchParams(searchParams);
  }, [replay.reset, searchParams, setSearchParams]);

  const handleHeaderMouseEnter = useCallback(() => {
    clearTimeout(hoverTimeoutRef.current);
    setHeaderHovered(true);
  }, []);

  const handleHeaderMouseLeave = useCallback(() => {
    hoverTimeoutRef.current = setTimeout(() => setHeaderHovered(false), 300);
  }, []);

  const handleHeaderClick = useCallback(() => {
    if (scrolledDown && headerCompact) {
      setHeaderPinned(true);
    }
  }, [scrolledDown, headerCompact]);

  const handleTimelineScrolled = useCallback((down: boolean) => {
    setScrolledDown(down);
    if (down) setHeaderPinned(false);
  }, []);

  const handleReplayKeydown = useCallback((e: KeyboardEvent) => {
    const SPEEDS: ReplaySpeed[] = [0.5, 1, 2, 4];

    switch (e.key) {
      case ' ':
        e.preventDefault();
        if (replay.state === 'playing') {
          replay.pause();
        } else {
          replay.play();
        }
        break;
      case 'ArrowLeft':
        replay.seekTo(replay.currentIndex - 1);
        break;
      case 'ArrowRight':
        replay.seekTo(replay.currentIndex + 1);
        break;
      case '[': {
        const idx = SPEEDS.indexOf(replay.speed);
        if (idx > 0) {
          replay.setSpeed(SPEEDS[idx - 1]);
        }
        break;
      }
      case ']': {
        const idx = SPEEDS.indexOf(replay.speed);
        if (idx < SPEEDS.length - 1) {
          replay.setSpeed(SPEEDS[idx + 1]);
        }
        break;
      }
      case 'r':
        replay.reset();
        break;
      case 'Escape':
        exitReplay();
        break;
    }
  }, [replay.state, replay.speed, replay.currentIndex, replay.pause, replay.play, replay.seekTo, replay.setSpeed, replay.reset, exitReplay]);

  useEffect(() => {
    if (!isReplayMode) return;
    window.addEventListener('keydown', handleReplayKeydown);
    return () => {
      window.removeEventListener('keydown', handleReplayKeydown);
    };
  }, [isReplayMode, handleReplayKeydown]);

  const startEditName = useCallback(() => {
    setEditNameValue(session?.name || '');
    setIsEditingName(true);
    setTimeout(() => nameInputRef.current?.focus(), 0);
  }, [session?.name]);

  const submitName = useCallback(() => {
    const trimmed = editNameValue.trim();
    if (trimmed && trimmed !== session?.name) {
      renameSession.mutate(trimmed);
    }
    setIsEditingName(false);
  }, [editNameValue, session?.name, renameSession]);

  const autoGenerateName = useCallback(() => {
    if (!events || events.length === 0) return;
    for (const event of events) {
      const data = event.data as Record<string, unknown> | undefined;
      if (data?.type === 'user' && typeof data.content === 'string') {
        let name = data.content.trim();
        if (name.length > 60) {
          name = name.slice(0, 60).replace(/\s+\S*$/, '').trim();
          if (name.length > 0) name += '...';
        }
        if (name) {
          setEditNameValue(name);
          setIsEditingName(true);
          setTimeout(() => nameInputRef.current?.focus(), 0);
        }
        return;
      }
    }
  }, [events]);

  if (sessionLoading) {
    return (
      <div className="py-12 text-center text-sm" style={{ color: 'var(--color-text-3)' }}>Loading session...</div>
    );
  }

  if (isError) {
    return (
      <div className="py-12 text-center text-sm" style={{ color: 'var(--color-error)' }}>
        Failed to load session: {error.message}
      </div>
    );
  }

  if (!session) {
    return (
      <div className="py-12 text-center text-sm" style={{ color: 'var(--color-text-3)' }}>Session not found</div>
    );
  }

  const displayEvents = isReplayMode ? replay.visibleEvents : events;
  const showReplayButton = session.status === 'terminated' && !isReplayMode && events && events.length > 0;
  const sessionDisplayName = session.name || `Session ${session.id.slice(0, 8)}`;

  return (
    <div className="mx-auto flex h-full max-w-5xl flex-col overflow-hidden">
      {/* Header — auto-collapses on scroll, expands on hover */}
      <div
        className="session-header flex-shrink-0"
        data-compact={headerCompact}
        data-overlay={!headerCompact && scrolledDown}
        onMouseEnter={handleHeaderMouseEnter}
        onMouseLeave={handleHeaderMouseLeave}
        onClick={handleHeaderClick}
      >
        {/* Compact strip (absolute, fades in when compact) */}
        <div className="session-header-compact">
          <div
            className="flex items-center gap-3 border-b px-1 pb-2 font-mono text-[11px]"
            style={{ borderColor: 'var(--color-border)', color: 'var(--color-text-3)' }}
          >
            <Link to={`/servers/${serverId}`} style={{ color: 'var(--color-text-3)', textDecoration: 'none', flexShrink: 0 }}>
              &larr; {serverId}
            </Link>
            <span style={{ color: 'var(--color-border-2)' }}>|</span>
            <span className="truncate font-medium" style={{ color: 'var(--color-text)' }}>{sessionDisplayName}</span>
            <span style={{ color: 'var(--color-border-2)' }}>|</span>
            <span>{MODEL_LABELS[session.model] ?? session.model}</span>
            <StatusDot status={session.status} />
            {isLive && (
              <span className="font-medium" style={{ color: 'var(--color-brand)' }} data-testid="live-indicator">
                {isStreaming ? 'STR' : 'LIVE'}
              </span>
            )}
            {isReplayMode && (
              <span className="font-medium" style={{ color: 'var(--color-brand)' }} data-testid="replay-indicator">
                RPL
              </span>
            )}
            {session.tokenUsage && (
              <>
                <span style={{ color: 'var(--color-border-2)' }}>|</span>
                <span>&darr;{formatTokens(session.tokenUsage.inputTokens)}</span>
                <span>&uarr;{formatTokens(session.tokenUsage.outputTokens)}</span>
              </>
            )}
            <div className="ml-auto flex items-center gap-2" style={{ flexShrink: 0 }}>
              {showReplayButton && (
                <button
                  type="button"
                  onClick={enterReplay}
                  className="rounded px-2 py-0.5 text-[10px] font-medium"
                  style={{ border: '1px solid var(--color-brand)', color: 'var(--color-brand)' }}
                  data-testid="enter-replay"
                >
                  Replay
                </button>
              )}
              {isReplayMode && (
                <button
                  type="button"
                  onClick={exitReplay}
                  className="rounded px-2 py-0.5 text-[10px] font-medium"
                  style={{ border: '1px solid var(--color-border-2)', color: 'var(--color-text-2)' }}
                  data-testid="exit-replay"
                >
                  Exit
                </button>
              )}
              <InterruptButton session={session} />
            </div>
          </div>
        </div>

        {/* Full header (normal flow, fades out when compact) */}
        <div className="session-header-full">
          <Link
            to={`/servers/${serverId}`}
            className="text-sm"
            style={{ color: 'var(--color-text-3)' }}
          >
            &larr; {serverId}
          </Link>

          <div className="mt-2 flex items-start justify-between">
            <div>
              {isEditingName ? (
                <div className="flex items-center gap-2">
                  <input
                    ref={nameInputRef}
                    type="text"
                    value={editNameValue}
                    onChange={(e) => setEditNameValue(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') submitName();
                      else if (e.key === 'Escape') setIsEditingName(false);
                    }}
                    onBlur={submitName}
                    maxLength={100}
                    disabled={renameSession.isPending}
                    className="rename-input rename-input-lg text-xl font-bold"
                    aria-label="Session name"
                  />
                </div>
              ) : (
                <div className="group/name flex items-center gap-2">
                  <h1 className="text-xl font-bold" style={{ color: 'var(--color-text)' }}>
                    {sessionDisplayName}
                  </h1>
                  {isAdmin && (
                    <button
                      type="button"
                      onClick={startEditName}
                      className="rename-icon-btn flex-shrink-0 opacity-0 transition-all group-hover/name:opacity-100"
                      aria-label="Rename session"
                    >
                      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L6.832 19.82a4.5 4.5 0 0 1-1.897 1.13l-2.685.8.8-2.685a4.5 4.5 0 0 1 1.13-1.897L16.863 4.487Zm0 0L19.5 7.125" />
                      </svg>
                    </button>
                  )}
                  {isAdmin && events && events.length > 0 && !session.name && (
                    <button
                      type="button"
                      onClick={autoGenerateName}
                      className="rename-icon-btn flex-shrink-0 opacity-0 transition-all group-hover/name:opacity-100"
                      aria-label="Auto-generate name"
                      title="Generate name from first message"
                    >
                      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904 9 18.75l-.813-2.846a4.5 4.5 0 0 0-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 0 0 3.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 0 0 3.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 0 0-3.09 3.09ZM18.259 8.715 18 9.75l-.259-1.035a3.375 3.375 0 0 0-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 0 0 2.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 0 0 2.455 2.456L21.75 6l-1.036.259a3.375 3.375 0 0 0-2.455 2.456Z" />
                      </svg>
                    </button>
                  )}
                </div>
              )}
              <div className="mt-1 flex items-center gap-3 text-sm" style={{ color: 'var(--color-text-3)' }}>
                <span className="font-mono text-xs">{session.id}</span>
                {isAdmin && isLive && session.status !== 'processing' ? (
                  <ModelSelector
                    current={session.model}
                    open={showModelMenu}
                    onToggle={() => setShowModelMenu((v) => !v)}
                    onSelect={(model) => {
                      setShowModelMenu(false);
                      if (model !== session.model) switchModel.mutate(model);
                    }}
                    isPending={switchModel.isPending}
                  />
                ) : (
                  <span>{MODEL_LABELS[session.model] ?? session.model}</span>
                )}
                {isLive && (awaitingDecision ? (
                  <span className="text-xs font-medium" style={{ color: 'var(--color-warn)' }} data-testid="live-indicator">
                    WAITING ON YOU
                  </span>
                ) : (
                  <span className="text-xs font-medium" style={{ color: 'var(--color-brand)' }} data-testid="live-indicator">
                    {isStreaming ? 'STREAMING' : 'LIVE'}
                  </span>
                ))}
                {session.startedBy && <StartedByLink serverId={serverId!} parentId={session.startedBy} />}
                <StartedSessionsLinks serverId={serverId!} sessionId={session.id} />
                <SessionWaits serverId={serverId!} sessionId={session.id} />
                {isAdmin && <NextLogin serverId={serverId!} enabled={server?.status === 'running'} />}
                {isReplayMode && (
                  <span className="text-xs font-medium" style={{ color: 'var(--color-brand)' }} data-testid="replay-indicator">
                    REPLAY
                  </span>
                )}
              </div>
              {/* What the index already says it is about (operator, 30/10/2026) */}
              <SessionFacets serverId={serverId!} sessionId={session.id} />
            </div>
            <div className="flex items-center gap-2">
              {showReplayButton && (
                <button
                  type="button"
                  onClick={enterReplay}
                  className="rounded-md px-3 py-1.5 text-xs font-medium"
                  style={{ border: '1px solid var(--color-brand)', color: 'var(--color-brand)' }}
                  data-testid="enter-replay"
                >
                  Replay
                </button>
              )}
              {isReplayMode && (
                <button
                  type="button"
                  onClick={exitReplay}
                  className="rounded-md px-3 py-1.5 text-xs font-medium"
                  style={{ border: '1px solid var(--color-border-2)', color: 'var(--color-text-2)' }}
                  data-testid="exit-replay"
                >
                  Exit Replay
                </button>
              )}
              <InterruptButton session={session} />
              <SessionStatusBadge status={session.status} />
            </div>
          </div>

          {/* Token usage and duration */}
          <div className="mt-3 flex items-center gap-4 text-xs" style={{ color: 'var(--color-text-3)' }} data-testid="status-bar">
            {session.tokenUsage && (
              <>
                <span>In: {formatTokens(session.tokenUsage.inputTokens)}</span>
                <span>Out: {formatTokens(session.tokenUsage.outputTokens)}</span>
                {session.tokenUsage.cacheReadTokens > 0 && (
                  <span>Cache: {formatTokens(session.tokenUsage.cacheReadTokens)}</span>
                )}
              </>
            )}
            {formatDuration(session) && (
              <span data-testid="duration">{formatDuration(session)}</span>
            )}
            {displayEvents && (
              <span style={{ color: 'var(--color-text-3)' }}>{displayEvents.length} events</span>
            )}
            {isLive && (
              <button
                type="button"
                onClick={resetHistory}
                className="inline-flex items-center gap-1 text-xs"
                style={{ color: 'var(--color-text-3)' }}
                onMouseEnter={(e) => (e.currentTarget.style.color = 'var(--color-text-2)')}
                onMouseLeave={(e) => (e.currentTarget.style.color = 'var(--color-text-3)')}
                title="Refresh events"
              >
                <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.992 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182M20.015 4.356v4.992" />
                </svg>
              </button>
            )}
          </div>

          <div className="mt-3 border-b" style={{ borderColor: 'var(--color-border)' }} />
        </div>
      </div>

      {/* Server stopped banner */}
      {serverStopped && (
        <div
          className="mx-1 mb-3 flex flex-shrink-0 items-center justify-between gap-3 rounded-lg border p-3 sm:p-4"
          style={{ borderColor: 'var(--color-warn)', backgroundColor: 'var(--color-warn-dim)' }}
          data-testid="server-stopped-banner"
        >
          <div className="flex items-center gap-2">
            <svg className="h-4 w-4 flex-shrink-0 sm:h-5 sm:w-5" style={{ color: 'var(--color-warn)' }} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M5.636 5.636a9 9 0 1 0 12.728 0M12 3v9" />
            </svg>
            <p className="text-xs font-medium sm:text-sm" style={{ color: 'var(--color-warn)' }}>
              Server is stopped
            </p>
          </div>
          {isAdmin && (
            <Button
              onClick={() => startServer.mutate(serverId!)}
              disabled={startServer.isPending}
              size="sm"
              style={{ backgroundColor: 'var(--color-success)', flexShrink: 0 }}
            >
              {startServer.isPending ? 'Starting...' : 'Start Server'}
            </Button>
          )}
        </div>
      )}

      {/* Timeline */}
      <div className="flex min-h-0 flex-1 flex-col">
        {!isReplayMode && eventsLoading && !events && (
          <div className="py-12 text-center text-sm" style={{ color: 'var(--color-text-3)' }}>Loading events...</div>
        )}
        {isReplayMode && replay.isLoading && (
          <div className="py-12 text-center text-sm" style={{ color: 'var(--color-text-3)' }}>Loading replay...</div>
        )}
        <SessionCardsContext.Provider value={isReplayMode ? noCards : cardAfter}>
          {displayEvents && <EventTimeline key={session.id} events={displayEvents} isProcessing={!isReplayMode && (session.status === 'processing' || isStreaming)} onScrolled={handleTimelineScrolled} />}
        </SessionCardsContext.Provider>
        {/* A proposal still waiting whose call is not among the events read: decided here all the same */}
        {!isReplayMode && unpaired.length > 0 && <section aria-label="Waiting on your decision" className="space-y-3 py-3" data-testid="session-decisions">{unpaired}</section>}
      </div>

      {/* Bottom controls */}
      <div className="flex-shrink-0 border-t pt-3" style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--color-surface)' }}>
        {isReplayMode && replay.totalEvents > 0 ? (
          <ReplayControls
            state={replay.state}
            speed={replay.speed}
            progress={replay.progress}
            currentIndex={replay.currentIndex}
            totalEvents={replay.totalEvents}
            onPlay={replay.play}
            onPause={replay.pause}
            onSetSpeed={replay.setSpeed}
            onSeek={replay.seekTo}
            onReset={replay.reset}
          />
        ) : (
          <SessionControls session={session} events={events} serverRunning={server?.status === 'running'} />
        )}
      </div>
    </div>
  );
}

/** A replay shows what happened, without the cards to decide */
const noCards = () => null;

const STATUS_DOT_MAP: Record<string, { color: string; pulse: boolean; label: string }> = {
  running:           { color: 'var(--color-success)', pulse: true,  label: 'Running' },
  active:            { color: 'var(--color-success)', pulse: true,  label: 'Active' },
  processing:        { color: 'var(--color-success)', pulse: true,  label: 'Processing' },
  starting:          { color: 'var(--color-warn)',    pulse: true,  label: 'Starting' },
  stopping:          { color: 'var(--color-warn)',    pulse: true,  label: 'Stopping' },
  waiting_for_input: { color: 'var(--color-warn)',    pulse: true,  label: 'Waiting' },
  switching:         { color: 'var(--color-warn)',    pulse: true,  label: 'Switching' },
  stopped:           { color: 'var(--color-idle)',    pulse: false, label: 'Stopped' },
  terminated:        { color: 'var(--color-error)',   pulse: false, label: 'Terminated' },
  idle:              { color: 'var(--color-idle)',    pulse: false, label: 'Idle' },
  error:             { color: 'var(--color-error)',   pulse: false, label: 'Error' },
};

function StatusDot({ status }: { status: string }) {
  const { color, pulse, label } = STATUS_DOT_MAP[status] ?? { color: 'var(--color-idle)', pulse: false, label: status };
  return (
    <span
      className={`inline-block h-[7px] w-[7px] rounded-full flex-shrink-0${pulse ? ' animate-pulse-dot' : ''}`}
      style={{ backgroundColor: color }}
      title={label}
    />
  );
}

const MODEL_OPTIONS: { value: string; label: string }[] = [
  { value: 'opus', label: 'Opus' },
  { value: 'sonnet', label: 'Sonnet' },
  { value: 'haiku', label: 'Haiku' },
];

function ModelSelector({
  current,
  open,
  onToggle,
  onSelect,
  isPending,
}: {
  current: string;
  open: boolean;
  onToggle: () => void;
  onSelect: (model: string) => void;
  isPending: boolean;
}) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function handleClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        onToggle();
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open, onToggle]);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={onToggle}
        disabled={isPending}
        className="inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-sm font-medium transition-colors"
        style={{
          border: '1px solid var(--color-border-2)',
          color: 'var(--color-text-2)',
          backgroundColor: 'var(--color-surface-2)',
        }}
        data-testid="model-selector"
      >
        {isPending ? 'Switching...' : MODEL_LABELS[current] ?? current}
        <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
        </svg>
      </button>
      {open && (
        <div
          className="animate-scale-in absolute left-0 top-full z-10 mt-1 min-w-[120px] rounded-lg py-1 shadow-lg"
          style={{ backgroundColor: 'var(--color-surface)', border: '1px solid var(--color-border-2)' }}
        >
          {MODEL_OPTIONS.map(({ value, label }) => (
            <button
              key={value}
              type="button"
              onClick={() => onSelect(value)}
              className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm transition-colors"
              style={{
                color: value === current ? 'var(--color-brand)' : 'var(--color-text-2)',
                fontWeight: value === current ? 500 : 400,
              }}
              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'var(--color-surface-2)')}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
            >
              {value === current && (
                <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="m4.5 12.75 6 6 9-13.5" />
                </svg>
              )}
              {value !== current && <span className="w-3" />}
              {label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
